import React, { useEffect, useState } from "react";
import axios from "axios";
import { API } from "../App";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Calendar, Users, MapPin, Clock, Filter, ArrowRight, Globe, ExternalLink } from "lucide-react";
import Header from "../components/layout/Header";
import Footer from "../components/layout/Footer";
import { Seo, breadcrumbJsonLd } from "../components/Seo";
import { formatDate } from "../utils/dateUtils";
import { COURSE_GROUPS, UCPLUS_NOTE, UCPLUS_COURSE_IDS, UCPLUS_LIVE_COURSE_IDS, getCourseGroupBySlug } from "../data/courseGroups";
import { INK, PRIMARY, Overline, AuroraBlue, AuroraLight, Reveal } from "../components/shared/kursusUi";
import { LANG_PACKS, langCoursePath, LANG_COURSE_MAP, COURSE_KEYS } from "../data/lang";
import { CONTACT } from "../data/votraLocations";

/** Sprogkode i URL'en (?sprog=en) → sprognavnet som holdene er gemt med i databasen. */
const LANG_TO_SESSION = { da: "dansk", en: "engelsk", ar: "arabisk", uk: "ukrainsk", fa: "persisk" };
const LANG_LABELS = { da: "Dansk", en: "Engelsk", ar: "Arabisk", uk: "Ukrainsk", fa: "Persisk" };
const LANG_FILTER_CODES = ["da", "en", "ar", "uk", "fa"];

// Convert UCplus "DD-MM-YYYY" date string to ISO "YYYY-MM-DD"
const ucplusDateToISO = (ddmmyyyy) => {
  if (!ddmmyyyy) return "";
  const p = ddmmyyyy.split("-");
  return p.length === 3 ? `${p[2]}-${p[1]}-${p[0]}` : ddmmyyyy;
};

// Map a backend course title to the matching course-group slug for /kurser/{slug}
// Accepts common typos (e.g. "Vogmand" — missing 'n') so a small admin mistake
// doesn't silently hide the whole hold from the calendar/detail pages.
const findGroupSlug = (courseTitle) => {
  if (!courseTitle) return null;
  const t = courseTitle.toLowerCase();
  // Exact title match first
  let match = COURSE_GROUPS.find(g => g.title?.toLowerCase() === t);
  if (match) return match.slug;
  // Then partial match on title or shortTitle
  match = COURSE_GROUPS.find(
    g => t.includes(g.shortTitle?.toLowerCase() || "") ||
         t.includes(g.title?.toLowerCase() || "") ||
         (g.shortTitle && g.shortTitle.toLowerCase().includes(t))
  );
  if (match) return match.slug;
  // Heuristic by keyword (accepts vognmand OR vogmand typo)
  const hasVognmand = t.includes("vognmand") || t.includes("vogmand");
  if (hasVognmand && t.includes("lastbil")) return "vognmand-lastbil";
  if (hasVognmand && (t.includes("varebil") || t.includes("vare"))) return "vognmand-varebil";
  if (t.includes("vb gu") || (t.includes("varebil") && t.includes("grund"))) return "vb-gu";
  if (t.includes("vb eu") || (t.includes("varebil") && t.includes("efter"))) return "vb-eu";
  if (t.includes("bab") || t.includes("flextrafik")) return "bab";
  if (t.includes("taxa")) return "taxa";
  if (t.includes("evs") || t.includes("evak")) return "evs";
  return null;
};

const CourseCalendarPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [courses, setCourses] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCourse, setSelectedCourse] = useState("all");
  const [hiddenCourses, setHiddenCourses] = useState([]);
  const urlLang = searchParams.get("sprog");
  const [selectedLanguage, setSelectedLanguage] = useState(
    urlLang && LANG_TO_SESSION[urlLang] ? urlLang : "all"
  );
  const courseSlugParam = searchParams.get("kursus");
  const langPack = LANG_PACKS[selectedLanguage] || null;

  useEffect(() => {
    fetchData();
  }, []);

  // Dyblink fra sprogsiderne: ?kursus=<kursusgruppe-slug> vælger kurset, når holdene er hentet
  useEffect(() => {
    if (!courseSlugParam || courses.length === 0) return;
    const match = courses.find((c) => findGroupSlug(c.title) === courseSlugParam);
    if (match) setSelectedCourse(match.id);
    else if (UCPLUS_LIVE_COURSE_IDS.includes(courseSlugParam)) setSelectedCourse(`ucplus-${courseSlugParam}`);
  }, [courseSlugParam, courses]);

  const handleLanguageChange = (code) => {
    setSelectedLanguage(code);
    const next = new URLSearchParams(searchParams);
    if (code === "all") next.delete("sprog");
    else next.set("sprog", code);
    setSearchParams(next, { replace: true });
  };

  const handleCourseChange = (id) => {
    setSelectedCourse(id);
    const next = new URLSearchParams(searchParams);
    next.delete("kursus");
    setSearchParams(next, { replace: true });
  };

  const fetchData = async () => {
    try {
      const T = 20000; // per-request timeout så ét hængende kald ikke blokerer alt
      // Kør uafhængige kald parallelt
      const [visRes, coursesRes] = await Promise.all([
        axios.get(`${API}/course-visibility`, { timeout: T }).catch(() => ({ data: { hidden: [] } })),
        axios.get(`${API}/courses`, { timeout: T }).catch(() => ({ data: [] })),
      ]);
      setHiddenCourses(visRes.data?.hidden || []);
      const courseList = coursesRes.data || [];
      setCourses(courseList);

      // Hent alle DB-sessioner OG UCplus-hold PARALLELT (i stedet for sekventielt)
      const dbSessionPromises = courseList.map((course) =>
        axios.get(`${API}/courses/${course.id}/sessions`, { timeout: T })
          .then((r) => r.data.map((s) => ({ ...s, course_title: course.title, course_id: course.id, course_image: course.image_url })))
          .catch(() => [])
      );
      const ucplusPromises = UCPLUS_LIVE_COURSE_IDS.map((slug) => {
        const group = getCourseGroupBySlug(slug);
        return axios.get(`${API}/ucplus-hold`, { params: { course: slug }, timeout: T })
          .then((res) => (res.data?.courses || []).map((h, idx) => ({
            id: `ucplus-${slug}-${h.kviknummer || h.id || idx}`,
            course_id: `ucplus-${slug}`,
            course_title: group?.title || slug,
            course_image: group?.image,
            start_date: ucplusDateToISO(h.startDate),
            end_date: ucplusDateToISO(h.endDate),
            location: h.location,
            time_type: h.teachingForm || "",
            language: h.language,
            ucplus_seats: parseInt(h.availableSeats, 10) || 0,
            hold_nummer: h.kviknummer,
            signupUrl: h.signupUrl,
            isUcplus: true,
          })))
          .catch(() => []);
      });

      const results = await Promise.all([...dbSessionPromises, ...ucplusPromises]);
      setSessions(results.flat());
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  // Filter and sort sessions
  const matchesLanguage = (s, code) =>
    code === "all" || String(s.language || "").toLowerCase().includes(LANG_TO_SESSION[code]);

  const visibleByCourseRules = sessions
    .filter(s => {
      // Hide sessions for courses the admin has hidden from the front page
      const slug = findGroupSlug(s.course_title);
      return !(slug && hiddenCourses.includes(slug));
    })
    .filter(s => new Date(s.start_date) >= new Date()); // Only future sessions

  const filteredSessions = visibleByCourseRules
    .filter(s => selectedCourse === "all" || s.course_id === selectedCourse)
    .filter(s => matchesLanguage(s, selectedLanguage))
    .sort((a, b) => new Date(a.start_date) - new Date(b.start_date));

  // Sprogvalg vises for de sprog, der findes på kommende hold/prøvedatoer — plus det
  // aktive valg, så dropdownen aldrig falder tilbage til "Alle sprog" uden at filtret er fjernet.
  const languageOptions = LANG_FILTER_CODES
    .map(code => ({
      code,
      label: LANG_LABELS[code],
      count: visibleByCourseRules.filter(s => (selectedCourse === "all" || s.course_id === selectedCourse) && matchesLanguage(s, code)).length,
    }))
    .filter(o => o.count > 0 || o.code === selectedLanguage);

  // Course options for the filter dropdown = internal DB courses + live UCplus courses
  // (exclude courses the admin has hidden from the front page)
  const ucplusFilterCourses = UCPLUS_LIVE_COURSE_IDS
    .filter(slug => !hiddenCourses.includes(slug))
    .map(slug => {
      const g = getCourseGroupBySlug(slug);
      return { id: `ucplus-${slug}`, title: g?.shortTitle || g?.title || slug };
    })
    .filter(fc => sessions.some(s => s.course_id === fc.id));
  const visibleCourses = courses.filter(c => {
    const slug = findGroupSlug(c.title);
    return !(slug && hiddenCourses.includes(slug));
  });
  const filterCourses = [...visibleCourses, ...ucplusFilterCourses];

  // Tilbage-link til den sprogside brugeren kom fra
  const langCourseKey = courseSlugParam
    ? COURSE_KEYS.find((k) => LANG_COURSE_MAP[k] === courseSlugParam)
    : null;
  const langBackPath = langPack
    ? (langCourseKey ? langCoursePath(langPack.code, langCourseKey) : `/${langPack.code}`)
    : "/";

  const seoTitle = langPack
    ? `Kursuskalender, hold og prøvedatoer på ${LANG_LABELS[langPack.code].toLowerCase()} | Votra`
    : "Kursuskalender, alle hold og prøvedatoer | Votra";

  return (
    <div className="min-h-screen flex flex-col" style={{ backgroundColor: 'hsl(210, 20%, 98%)' }}>
      <div className="votra-grain" aria-hidden />
      <Seo
        title={seoTitle}
        description="Se alle kommende hold og prøvedatoer hos Votra, filtrer på kursus og på sprog (dansk, engelsk, arabisk, ukrainsk, persisk) og se ledige pladser."
        path="/kalender"
        jsonLd={breadcrumbJsonLd([{ name: "Forside", path: "/" }, { name: "Kursuskalender", path: "/kalender" }])}
      />
      <Header />

      {/* Hero — dark ink + blue aurora */}
      <section className="hero-light relative overflow-hidden" style={{ backgroundColor: '#F4F6FB' }}>
        <AuroraLight />
        <div className="fx-scan"><span /></div>
        <div className="container mx-auto px-4 md:px-6 relative z-10 py-16 md:py-24">
          <Reveal><Overline light>Kursuskalender</Overline></Reveal>
          <Reveal i={1}>
            <h1 className="font-display text-4xl md:text-5xl font-medium text-white leading-tight mb-4 flex items-center gap-4">
              <Calendar className="h-9 w-9" style={{ color: 'hsl(200,100%,72%)' }} />
              Kursuskalender
            </h1>
          </Reveal>
          <Reveal i={2}>
            <p className="text-lg text-slate-300 leading-relaxed max-w-2xl">
              Se alle kommende kurser og find det der passer til dig.
            </p>
          </Reveal>
        </div>
      </section>

      <div className="container mx-auto px-4 md:px-6 py-8 flex-1">
        {/* Filter */}
        <div className="mb-6 flex flex-wrap items-center gap-3 sm:gap-4 bg-white p-4 rounded-xl border border-slate-200">
          <div className="flex items-center gap-2">
            <Filter className="h-5 w-5 text-slate-500" />
            <span className="text-sm font-medium text-slate-600">Filtrer:</span>
          </div>
          <select
            value={selectedCourse}
            onChange={(e) => handleCourseChange(e.target.value)}
            className="w-full sm:w-auto max-w-full px-4 py-2 rounded-lg border border-slate-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[hsl(210,100%,45%)]"
            data-testid="course-filter"
          >
            <option value="all">{`Alle kurser (${filteredSessions.length} datoer)`}</option>
            {filterCourses.map(course => {
              const count = sessions.filter(s => s.course_id === course.id && new Date(s.start_date) >= new Date()).length;
              const g = COURSE_GROUPS.find(gr => gr.slug === findGroupSlug(course.title));
              const unit = g?.selfStudy ? (count === 1 ? "prøvedato" : "prøvedatoer") : "hold";
              return (
                <option key={course.id} value={course.id}>{`${course.title} (${count} ${unit})`}</option>
              );
            })}
          </select>
          <select
            value={selectedLanguage}
            onChange={(e) => handleLanguageChange(e.target.value)}
            className="w-full sm:w-auto max-w-full px-4 py-2 rounded-lg border border-slate-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[hsl(210,100%,45%)]"
            data-testid="language-filter"
            aria-label="Filtrer efter sprog"
          >
            <option value="all">Alle sprog</option>
            {languageOptions.map(o => (
              <option key={o.code} value={o.code}>{`${o.label} (${o.count})`}</option>
            ))}
          </select>
        </div>

        {/* Kom du fra en sprogside? Så står beskeden på dit eget sprog. */}
        {langPack && (
          <div
            className="mb-6 rounded-xl border border-slate-200 bg-white p-4 flex flex-wrap items-center gap-x-4 gap-y-2"
            dir={langPack.dir}
            lang={langPack.htmlLang}
            data-testid="calendar-lang-notice"
          >
            <span className="inline-flex items-center gap-2 text-sm text-slate-700">
              <Globe className="h-4 w-4" style={{ color: PRIMARY }} />
              {selectedCourse === "all" ? langPack.ui.dates.calendarNoticeAll : langPack.ui.dates.calendarNotice}
            </span>
            <Link
              to={langBackPath}
              className="text-sm font-semibold hover:underline"
              style={{ color: PRIMARY }}
              data-testid="calendar-lang-back"
            >
              {langPack.ui.dates.calendarBack}
            </Link>
          </div>
        )}

        {/* Sessions List */}
        {loading ? (
          <div className="text-center py-20">
            <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-[hsl(210,100%,45%)] border-r-transparent"></div>
            <p className="mt-4 text-slate-600">Indlæser kurser...</p>
          </div>
        ) : filteredSessions.length === 0 ? (
          <div className="text-center py-20 bg-white rounded-xl border border-slate-200" data-testid="calendar-empty">
            <Calendar className="h-12 w-12 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-600">Ingen kommende kurser fundet.</p>
            {langPack && (
              <div dir={langPack.dir} lang={langPack.htmlLang} className="mt-4 max-w-xl mx-auto px-4">
                <p className="text-slate-600" data-testid="calendar-empty-lang">{langPack.ui.dates.empty}</p>
                <a
                  href={`tel:${CONTACT.phone}`}
                  className="inline-flex items-center gap-2 mt-4 px-5 py-2.5 rounded-full font-semibold text-white"
                  style={{ backgroundColor: PRIMARY }}
                  data-testid="calendar-empty-call"
                >
                  {langPack.ui.cta.call}
                </a>
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-4">
            {filteredSessions.map((session) => {
              const availableSpots = session.isUcplus
                ? session.ucplus_seats
                : (session.total_spots - session.booked_spots);
              const isFull = availableSpots <= 0;
              // Resolve image: prefer category image from COURSE_GROUPS (consistent with homepage),
              // fall back to backend course_image only if no slug matches.
              const slug = findGroupSlug(session.course_title);
              const group = slug ? COURSE_GROUPS.find(g => g.slug === slug) : null;
              const categoryImage = group?.image || null;
              const imageUrl = categoryImage || session.course_image;
              // Vognmandsforløbene har ingen undervisning: datoen er en prøvedato.
              const isExamDate = !!group?.selfStudy;

              return (
                <div
                  key={session.id}
                  className="bg-white rounded-2xl border border-slate-200/80 overflow-hidden shadow-[0_8px_30px_rgba(15,40,90,0.06)] transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_18px_44px_rgba(15,40,90,0.14)]"
                >
                  <div className="flex flex-col md:flex-row">
                    {/* Course Image (always shown - falls back to category image) */}
                    {imageUrl && (
                      <div className="md:w-48 h-32 md:h-auto flex-shrink-0 bg-slate-100">
                        <img
                          src={imageUrl}
                          alt={session.course_title}
                          className="w-full h-full object-cover"
                        />
                      </div>
                    )}
                    
                    {/* Session Info */}
                    <div className="flex-1 p-5">
                      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                        <div className="flex-1">
                          <h3 className="font-display text-xl font-medium mb-2 text-slate-900">
                            {session.course_title}
                          </h3>
                          
                          <div className="flex flex-wrap gap-4 text-sm text-slate-600">
                            <div className="flex items-center gap-1.5">
                              <Calendar className="h-4 w-4 text-[hsl(210,100%,45%)]" />
                              {isExamDate ? (
                                <span className="font-medium">Prøvedato {formatDate(session.start_date)}</span>
                              ) : (
                                <>
                                  <span className="font-medium">{formatDate(session.start_date)}</span>
                                  <span className="text-slate-400">til</span>
                                  <span className="font-medium">{formatDate(session.end_date)}</span>
                                </>
                              )}
                            </div>
                            
                            {session.location && (
                              <div className="flex items-center gap-1.5">
                                <MapPin className="h-4 w-4 text-slate-400" />
                                <span>{session.location}</span>
                              </div>
                            )}
                            
                            {isExamDate ? (
                              <div className="flex items-center gap-1.5">
                                <Clock className="h-4 w-4 text-slate-400" />
                                <span>Mødetid oplyses i prøveindkaldelsen</span>
                              </div>
                            ) : (
                              <div className="flex items-center gap-1.5">
                                <Clock className="h-4 w-4 text-slate-400" />
                                <span className="capitalize">{session.time_type}</span>
                              </div>
                            )}

                            {session.language && (
                              <div className="flex items-center gap-1.5">
                                <Globe className="h-4 w-4 text-slate-400" />
                                <span>{session.language}</span>
                              </div>
                            )}
                            
                            <div className="flex items-center gap-1.5">
                              <Users className="h-4 w-4 text-slate-400" />
                              <span className={isFull ? 'text-red-600' : availableSpots <= 3 ? 'text-amber-600' : 'text-emerald-600'}>
                                {availableSpots} ledige pladser
                              </span>
                            </div>
                          </div>

                          {session.hold_nummer && (
                            <div className="mt-2">
                              <span className="text-xs px-2 py-1 rounded bg-slate-100 text-slate-600 font-mono">
                                {isExamDate ? "Prøve" : "Hold"}: {session.hold_nummer}
                              </span>
                            </div>
                          )}

                          {slug && UCPLUS_COURSE_IDS.includes(slug) && (
                            <p className="mt-3 text-xs text-slate-500" data-testid="ucplus-note-calendar">
                              {UCPLUS_NOTE}
                            </p>
                          )}
                        </div>
                        
                        {/* Action */}
                        <div className="flex items-center gap-3">
                          {session.isUcplus ? (
                            <a
                              href={session.signupUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex items-center gap-2 px-5 py-2.5 rounded-full font-medium text-white transition-transform hover:-translate-y-0.5"
                              style={{ backgroundColor: 'hsl(210, 100%, 45%)' }}
                              data-testid={`calendar-ucplus-signup-${session.hold_nummer || session.id}`}
                            >
                              Tilmeld Nu
                              <ExternalLink className="h-4 w-4" />
                            </a>
                          ) : (
                            <button
                              onClick={() => {
                                const targetSlug = findGroupSlug(session.course_title);
                                if (targetSlug) navigate(`/kurser/${targetSlug}`);
                                else navigate(`/course/${session.course_id}`);
                              }}
                              className="flex items-center gap-2 px-5 py-2.5 rounded-full font-medium text-white transition-transform hover:-translate-y-0.5"
                              style={{ backgroundColor: isFull ? 'hsl(24, 94%, 50%)' : 'hsl(210, 100%, 45%)' }}
                              data-testid={`calendar-signup-${session.hold_nummer || session.id}`}
                            >
                              {isFull ? 'Venteliste' : isExamDate ? 'Tilmeld prøve' : 'Tilmeld'}
                              <ArrowRight className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
};

export default CourseCalendarPage;
